import { DB_RETRY_OPTIONS } from '../constants';
import { AppError, AsyncResult, withRetry, retryable } from "@shared/utils";

/**
 * @group Functions
 * 
 * @summary
 * <blockquote>Executes an async function using exponential backoff retry logic with proper Result pattern typing</blockquote>
 *
 * @example
 * ```typescript
 * import { retryableQuery } from '@shared/db';
 * 
 * const result = await retryableQuery(() => db.select().from(usersTable));
 * result.match({
 *   ok: (users) => console.log('Users:', users),
 *   err: (error) => console.error('Query failed:', error.errorMessage)
 * });
 * ```
 * 
 * @typeParam T - The type of the successful result.
 * @param fn - The async function to execute.
 * @returns A Promise resolving to a Result with either the successful value or the error.
 */
/**
 * @internal
 */
export async function retryableQuery<T = unknown>(
  fn: () => Promise<T>
): AsyncResult<T, AppError> {
  return withRetry(retryable(fn), DB_RETRY_OPTIONS);
}
